import { ReactNode, Ref } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { TableRow } from './row';

export type TableRowData<T extends string, AdditionalCallbackFields extends object = object> = {
  id: string;
} & AdditionalCallbackFields &
  Record<
    T,
    | ((id: string, additionalFields: AdditionalCallbackFields) => ReactNode)
    | null
    | number
    | ReactNode
    | string
  >;

export type TableHeaderRowData<T extends string> = Record<T, number | ReactNode | string>;

export type TableProps<T extends string, AdditionalCallbackFields extends object> = {
  additionalCellContent?: RequireAtLeastOne<Record<T, (id: string) => ReactNode>>;
  className?: string;
  columnOrder: T[];
  columnsTitle: Partial<TableHeaderRowData<T>> | TableHeaderRowData<T>;
  ref?: Ref<HTMLTableElement>;

  tHeadClassNames?: {
    className?: string;
    contentClassName?: ({ general?: string } & PartialRecord<T, string>) | string;
    labelClassName?: ({ general?: string } & PartialRecord<T, string>) | string;
    thClassName?: ({ general?: string } & PartialRecord<T, string>) | string;
    trClassName?: string;
  };
} & Optional<
  {
    rows: TableRowData<T, AdditionalCallbackFields>[];
    tBodyClassNames?: {
      className?: string;
      spanClassName?: string;
      tdClassName?: string;
      trClassName?: string;
    };
  },
  { children: ReactNode }
>;

export const Table = <TColumn extends string, AdditionalCallbackFields extends object = object>({
  columnOrder,
  columnsTitle,
  rows = [],
  className,
  tBodyClassNames,
  tHeadClassNames,
  additionalCellContent,
  children,
  ref,
}: TableProps<TColumn, AdditionalCallbackFields>) => {
  return (
    <table
      className={twMerge('h-fit w-full shrink-0 overflow-hidden text-nowrap rounded-md', className)}
      ref={ref}
    >
      <thead
        className={twMerge(
          'h-11 rounded-t-md border-b border-[#F2F2F2] bg-[#F9FAFB]',
          tHeadClassNames?.className,
        )}
      >
        <tr className={tHeadClassNames?.trClassName}>
          {columnOrder.map((column) => {
            const translation = columnsTitle[column];

            const thClassName =
              typeof tHeadClassNames?.thClassName === 'string'
                ? tHeadClassNames?.thClassName
                : twMerge(
                    tHeadClassNames?.thClassName?.[column],
                    tHeadClassNames?.thClassName?.general,
                  );

            const contentClassName =
              typeof tHeadClassNames?.contentClassName === 'string'
                ? tHeadClassNames?.contentClassName
                : twMerge(
                    tHeadClassNames?.contentClassName?.[column],
                    tHeadClassNames?.contentClassName?.general,
                  );

            const labelClassName =
              typeof tHeadClassNames?.labelClassName === 'string'
                ? tHeadClassNames?.labelClassName
                : twMerge(
                    tHeadClassNames?.labelClassName?.[column],
                    tHeadClassNames?.labelClassName?.general,
                  );

            return (
              <th className={twMerge('relative px-2', thClassName)} key={column}>
                <div
                  className={twMerge('flex h-full w-full items-center text-left', contentClassName)}
                >
                  <span
                    className={twJoin('text-label-md font-medium text-[#172335]', labelClassName)}
                  >
                    {translation}
                  </span>
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody className={twMerge('divide-b divide-y divide-[#F2F2F2]', tBodyClassNames?.className)}>
        {children
          ? children
          : rows.map((row, i) => (
              <TableRow
                additionalCellContent={additionalCellContent}
                columnOrder={columnOrder}
                contentClassName={tBodyClassNames?.tdClassName}
                fields={row}
                key={`${row.id}_${i}`}
                labelClassName={tBodyClassNames?.spanClassName}
                trClassName={tBodyClassNames?.trClassName}
              />
            ))}
      </tbody>
    </table>
  );
};
